// What a home-dashboard notice card is allowed to ask of the component that hosts
// it, and the shape every notice controller has in common.
//
// The dashboard shows a stack of small `cs-info-card` notices above the day's item
// list. Which of them exist, the order they are drawn in and which may appear
// together is decided in cs-day-items-notice-stack.ts. This file is about the
// other half: each notice owning its own state, its own refresh, its own dismiss
// and action handlers, and whatever watcher or listener it needs, instead of
// having all of that spread across CsDayItemsComponent by hand (#5236).
//
// ## Why these are plain classes and not Angular components
//
// A notice controller must not import `@angular/core`, directly or through
// anything else. The client's only unit-test runner is mocha with ts-node
// (`client/package.json`'s `test` script) and it cannot load a file that
// transitively pulls in Angular's ESM exports — the same constraint the pure
// decision helpers beside this file already work under, stated in the header of
// cs-day-items-update-card.ts. Turning each notice into a child Angular component
// would give up that cheap test path for nothing.
//
// So a controller never takes `ChangeDetectorRef`, `NavController`,
// `TelemetryService` or `DestroyGuard`. It takes the two host abilities below,
// plus narrow structural interfaces or plain callbacks for anything else it
// needs. cs-day-items-load-recovery.ts in this folder is the same idea, called
// once instead of held as an object.

/**
 * The two things every notice needs from the component that owns it.
 *
 * A notice writes its own fields from watchers, listeners and post-`await` code —
 * all of which run outside any template evaluation, so Angular has to be told the
 * view is dirty. `isDestroyed` is checked before doing so, because calling
 * `markForCheck` on a destroyed view raises NG0911.
 */
export interface DayItemsNoticeHost {
  /** True once the host component has been destroyed. Check before every write. */
  isDestroyed(): boolean;
  /** Tell Angular this notice's fields changed and the view needs re-rendering. */
  markForCheck(): void;
}

/**
 * Tell the host a notice's fields changed, unless the host is already destroyed.
 *
 * Every notice calls this after writing its own state, instead of calling
 * `host.markForCheck()` directly, so the NG0911 guard is written once here.
 */
export function markNoticeHostForCheck(host: DayItemsNoticeHost): void {
  if (host.isDestroyed()) return;
  host.markForCheck();
}

/**
 * The only surface the host needs from a notice, whatever kind of notice it is.
 *
 * `wantsToShow` is this notice's own answer to "do I have something to say right
 * now?", which `decideVisibleNotices` gathers along with every other notice's
 * before deciding what is actually drawn. `dispose` releases whatever the notice
 * created — a `DocWatcher`, a platform listener — and is called once from the
 * component's `ngOnDestroy`, for every notice, in one loop.
 */
export interface DayItemsNoticeController {
  readonly wantsToShow: boolean;
  dispose(): void;
}
